const API_URL = 'https://lyfy.ltd';

interface FeedPost {
  id?: number;
  title: string;
  content: string;
  image_url?: string;
  author: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data?: any;
}

const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeout = 5000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        'Origin': window.location.origin,
        ...options.headers,
      },
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

export const feedService = {
  getPosts: async () => {
    try {
      console.log('Fetching posts from:', `${API_URL}/manage_feed.php?action=get`);
      const response = await fetch(`${API_URL}/manage_feed.php?action=get`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Received posts data:', data);
      
      // If we got an error response
      if (data.success === false) {
        console.error('API error:', data.message);
        return [];
      }
      
      // If it's an array, it's our posts
      if (Array.isArray(data)) {
        return data;
      }
      
      console.error('Unexpected response format:', data);
      return [];
    } catch (error) {
      console.error('Error fetching posts:', error);
      return [];
    }
  },

  createPost: async (post: FeedPost, adminKey: string) => {
    try {
      console.log('Creating post with data:', post);
      const formData = new FormData();
      
      // Add post data
      Object.entries(post).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          console.log(`Adding form data: ${key} = ${value.substring ? value.substring(0, 50) + '...' : value}`);
          formData.append(key, String(value));
        }
      });
      
      // Add admin key and action
      formData.append('adminKey', adminKey);
      formData.append('action', 'create');

      console.log('Sending request to:', `${API_URL}/manage_feed.php`);
      const response = await fetch(`${API_URL}/manage_feed.php`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Received response:', result);

      if (!result.success) {
        throw new Error(result.message || 'Failed to create post');
      }

      return result;
    } catch (error) {
      console.error('Error in createPost:', error);
      throw error;
    }
  },

  updatePost: async (post: FeedPost, adminKey: string) => {
    try {
      const formData = new FormData();
      
      // Add post data
      Object.entries(post).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
      
      // Add admin key and action
      formData.append('adminKey', adminKey);
      formData.append('action', 'update');

      const response = await fetch(`${API_URL}/manage_feed.php`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to update post');
      }

      return result;
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  },

  deletePost: async (id: number, adminKey: string) => {
    try {
      const formData = new FormData();
      formData.append('id', String(id));
      formData.append('adminKey', adminKey);
      formData.append('action', 'delete');

      const response = await fetch(`${API_URL}/manage_feed.php`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete post');
      }

      return result;
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  }
};
